import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  LayoutDashboard,
  List,
  Loader2,
  FileText,
} from 'lucide-react';
import { apiFetch } from './apiBase';
import ElementExceptions from './ElementExceptions';
import ReconNotes from './ReconNotes';
import ReconMetricDetail from './ReconMetricDetail';
import {
  METRIC_GROUPS,
  elementBreakdownTotal,
  flattenMetrics,
} from './reconShared';

export default function ReconResults({ refreshKey = 0, onNavigate }) {
  const [resultsTab, setResultsTab] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [hasResults, setHasResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState('current');
  const [activeMetric, setActiveMetric] = useState(null);
  const [exceptionRefresh, setExceptionRefresh] = useState(0);

  useEffect(() => {
    setExceptionRefresh((k) => k + 1);
  }, [refreshKey, selectedRunId]);

  useEffect(() => {
    let cancelled = false;
    async function loadRuns() {
      try {
        const d = await apiFetch('/api/recon/runs');
        if (!cancelled) setRuns(d.runs || []);
      } catch {
        if (!cancelled) setRuns([]);
      }
    }
    loadRuns();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  useEffect(() => {
    let cancelled = false;
    async function loadSummary() {
      setLoading(true);
      try {
        const q =
          selectedRunId && selectedRunId !== 'current'
            ? `?runId=${encodeURIComponent(selectedRunId)}`
            : '';
        const { summary: s } = await apiFetch(`/api/recon/summary${q}`);
        if (cancelled) return;
        if (s?.reconMode === 'element-wage-type' || s?.grain?.includes('ORACLE_ELEMENT') || s?.payPeriod) {
          setSummary(s);
          setHasResults(true);
        } else {
          setSummary(null);
          setHasResults(false);
        }
      } catch {
        if (!cancelled) {
          setSummary(null);
          setHasResults(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadSummary();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, selectedRunId]);

  const byElement = hasResults ? summary?.byElement || summary?.byClassification || {} : {};

  const runLabel = summary?.runAt
    ? new Date(summary.runAt).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-16 text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading results…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="section-title">Reconciliation output</p>
            <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-100">Results</h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Summary metrics, person coverage, and exception detail. Select a past run by pay period.
            </p>
            {runs.length > 0 && (
              <div className="mt-4">
                <label className="block text-xs font-medium text-slate-500">Run history</label>
                <select
                  className="run-history-select mt-1 max-w-md"
                  value={selectedRunId}
                  onChange={(e) => {
                    setSelectedRunId(e.target.value);
                    setActiveMetric(null);
                  }}
                >
                  {runs.map((r) => (
                    <option key={r.id} value={r.id}>
                      Pay period {r.payPeriod || '?'} · {r.exceptionCount ?? '?'} exceptions ·{' '}
                      {r.createdAt
                        ? new Date(r.createdAt).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })
                        : '—'}
                      {r.reconScopeLabel ? ` · ${r.reconScopeLabel}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {hasResults && summary && (
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="badge-success">Pay period {summary.payPeriod}</span>
                {summary.reconScopeLabel && (
                  <span className="badge-neutral">{summary.reconScopeLabel}</span>
                )}
                {summary.hoursReconEnabled && (
                  <span className="badge-neutral">Hours recon active</span>
                )}
                {runLabel && (
                  <span className="badge-neutral inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {runLabel}
                  </span>
                )}
                {summary.exceptionCount != null && (
                  <span className="badge-warning">
                    {summary.exceptionCount.toLocaleString()} exceptions
                  </span>
                )}
              </div>
            )}
          </div>
          {hasResults && (
            <div className="tab-bar shrink-0">
              <button
                type="button"
                onClick={() => setResultsTab('summary')}
                className={resultsTab === 'summary' ? 'tab-pill-active' : 'tab-pill-inactive'}
              >
                <LayoutDashboard className="h-4 w-4" />
                Summary
              </button>
              <button
                type="button"
                onClick={() => setResultsTab('exceptions')}
                className={resultsTab === 'exceptions' ? 'tab-pill-active' : 'tab-pill-inactive'}
              >
                <List className="h-4 w-4" />
                Exceptions
                {summary?.exceptionCount > 0 && (
                  <span className="ml-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {summary.exceptionCount > 999 ? '999+' : summary.exceptionCount}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </header>

      {!hasResults && (
        <div className="empty-state-panel">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-800 ring-1 ring-slate-700">
            <FileText className="h-7 w-7 text-cyan-500/80" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-slate-100">No results yet</h3>
          <p className="mt-2 max-w-md text-sm text-slate-400">
            Add a payroll period, then run reconciliation from Payroll Recon. When the run
            finishes, summary metrics and exceptions appear here automatically.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => onNavigate?.('periods')}
              className="btn-secondary"
            >
              Add a payroll period
            </button>
            <button
              type="button"
              onClick={() => onNavigate?.('recon')}
              className="btn-primary"
            >
              Go to Payroll Recon
            </button>
          </div>
        </div>
      )}

      {hasResults && summary && resultsTab === 'summary' && (
        <>
          <ReconNotes
            notes={summary.reconNotes}
            linesNormalized={summary.registerLinesNormalized}
          />

          <div className="space-y-6">
            {(summary.toleranceRule || summary.hoursToleranceRule) && (
              <p className="text-sm text-slate-400">
                {summary.toleranceRule}
                {summary.hoursToleranceRule && <> · {summary.hoursToleranceRule}</>}
                {summary.dollarExceptionCount != null && summary.hoursExceptionCount != null && (
                  <>
                    {' '}
                    · {summary.dollarExceptionCount} $ exceptions, {summary.hoursExceptionCount} hours
                  </>
                )}
              </p>
            )}

            {METRIC_GROUPS.map((group) => (
              <div key={group.title}>
                <div className="mb-3 flex items-center gap-2">
                  <group.icon className="h-4 w-4 text-cyan-500/80" />
                  <div>
                    <p className="text-sm font-semibold text-slate-100">{group.title}</p>
                    <p className="text-xs text-slate-500">{group.description}</p>
                  </div>
                </div>
                <div
                  className={`grid gap-3 ${
                    group.metrics.length > 2 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2'
                  }`}
                >
                  {group.metrics.map((m) => {
                    const value = m.value(summary);
                    const warn =
                      m.warn &&
                      (typeof value === 'number'
                        ? value > 0
                        : Array.isArray(value)
                          ? value.length > 0
                          : false);
                    return (
                      <MetricCard
                        key={m.key}
                        label={m.label}
                        value={value}
                        warn={warn}
                        active={activeMetric === m.key}
                        onClick={() => setActiveMetric((cur) => (cur === m.key ? null : m.key))}
                      />
                    );
                  })}
                </div>
              </div>
            ))}

            {activeMetric && (
              <ReconMetricDetail
                key={`${activeMetric}-${exceptionRefresh}`}
                metric={activeMetric}
                label={
                  flattenMetrics().find((m) => m.key === activeMetric)?.label || activeMetric
                }
                hasReconResults
                onClose={() => setActiveMetric(null)}
                refreshKey={exceptionRefresh}
              />
            )}

            {Object.keys(byElement).length > 0 && (
              <div className="card-panel overflow-hidden">
                <div className="section-header">
                  <h3 className="font-semibold text-slate-100">Exceptions by Oracle element</h3>
                  <p className="text-xs text-slate-500">Sorted by total issue count</p>
                </div>
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Element</th>
                        <th className="text-right">Missing ADP</th>
                        <th className="text-right">Missing Oracle</th>
                        <th className="text-right">$ Mismatch</th>
                        <th className="text-right">Unmapped</th>
                        <th className="text-right">Hrs issues</th>
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(byElement)
                        .sort((a, b) => elementBreakdownTotal(b[1]) - elementBreakdownTotal(a[1]))
                        .slice(0, 50)
                        .map(([name, row]) => {
                          const counts =
                            typeof row === 'object' && row !== null
                              ? row
                              : {
                                  MissingInADP: 0,
                                  MissingInOracle: 0,
                                  AmountMismatch: row || 0,
                                  Unmapped: 0,
                                };
                          const hrs =
                            (counts.HoursMismatch || 0) +
                            (counts.MissingInG2 || 0) +
                            (counts.MissingAdpHours || 0);
                          return (
                            <tr key={name}>
                              <td className="font-medium text-slate-200">{name}</td>
                              <td className="text-right font-mono tabular-nums">
                                {counts.MissingInADP ?? 0}
                              </td>
                              <td className="text-right font-mono tabular-nums">
                                {counts.MissingInOracle ?? 0}
                              </td>
                              <td className="text-right font-mono tabular-nums">
                                {counts.AmountMismatch ?? 0}
                              </td>
                              <td className="text-right font-mono tabular-nums">
                                {counts.Unmapped ?? 0}
                              </td>
                              <td className="text-right font-mono tabular-nums">{hrs}</td>
                              <td className="text-right font-mono font-semibold tabular-nums">
                                {elementBreakdownTotal(counts)}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                {Object.keys(byElement).length > 50 && (
                  <p className="border-t border-slate-700 px-5 py-2 text-xs text-slate-500">
                    Showing top 50 of {Object.keys(byElement).length} elements — open the Exceptions
                    tab for full browsing.
                  </p>
                )}
              </div>
            )}

            {runs.length > 1 && (
              <p className="text-xs text-slate-500">
                {runs.length} saved runs available — use the dropdown above to compare pay periods.
              </p>
            )}
          </div>
        </>
      )}

      {hasResults && resultsTab === 'exceptions' && (
        <ElementExceptions
          hasReconResults
          refreshKey={exceptionRefresh}
          runId={selectedRunId}
        />
      )}
    </div>
  );
}

function MetricCard({ label, value, warn, active, onClick }) {
  const display =
    value == null ? '—' : typeof value === 'object' ? (Array.isArray(value) ? value.length : '—') : value;
  const num = typeof display === 'number';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`stat-card stat-card-clickable ${
        warn ? 'border-l-4 border-l-amber-500' : 'border-l-4 border-l-cyan-500'
      } ${active ? 'stat-card-active' : ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
        {warn ? (
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
        ) : (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
        )}
      </div>
      <p className="mt-2 text-3xl font-bold tabular-nums tracking-tight text-slate-50">
        {num ? display.toLocaleString() : display}
      </p>
      <p className="mt-2 text-xs font-medium text-slate-500">View details →</p>
    </button>
  );
}
